import React, { useCallback, useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Building2, FileText, HandCoins, HeartHandshake, Home, Briefcase, ShieldCheck } from "lucide-react";
import { apiClient } from "@/lib/auth";
import { useFloatingWindows } from "@/lib/floatingWindows";

export const GRANT_ORDER = ["housed", "employed", "pillars", "subsidized", "conduct"];
const ICONS = { housed: Home, employed: Briefcase, pillars: ShieldCheck, subsidized: HandCoins, conduct: HeartHandshake };

export const fmtGrant = (t) => {
  if (!t) return "—";
  if (t.format === "currency") {
    const n = Number(t.value || 0);
    return Math.abs(n) >= 1000 ? `$${Math.round(n).toLocaleString()}` : `$${n.toFixed(2)}`;
  }
  if (t.format === "score") return `${Number(t.value || 0).toFixed(1)}`;
  return Number(t.value || 0).toLocaleString();
};

export function useGrantOutcomes(range) {
  const [s, setS] = useState({ data: null, error: null });
  const key = range ? `${range.date_from}:${range.date_to}` : "trailing";
  const load = useCallback(() => apiClient.get("/grants/outcomes", { params: range || {} })
    .then(({ data }) => setS({ data, error: null }))
    .catch((e) => setS((x) => ({ ...x, error: e?.response?.data?.detail || "Couldn't load grant outcomes" }))), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); if (range) return undefined; const t = setInterval(load, 30000); return () => clearInterval(t); }, [load, range]);
  return s;
}

export function Trend({ delta }) {
  if (delta == null) return null;
  const up = delta >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return <span data-testid="grant-conduct-delta" className={`inline-flex items-center gap-0.5 font-mono text-xs ${up ? "text-emerald-400" : "text-red-400"}`}><Icon className="w-3.5 h-3.5" />{up ? "+" : ""}{delta} pts vs prior 30d</span>;
}

function Tile({ id, t, onOpen }) {
  const Icon = ICONS[id] || FileText;
  return (
    <button type="button" onClick={onOpen} data-testid={`grant-tile-${id}`}
      className="nds-card p-5 text-left text-nex-navy hover:border-nex-violet hover:shadow-[0_18px_40px_rgba(124,92,255,0.20)] transition-[border-color,box-shadow] flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div className="nds-label">{t.label}</div>
        <div className="w-9 h-9 shrink-0 grid place-items-center bg-nex-navy text-nex-goldbright rounded-sm"><Icon className="w-4 h-4" strokeWidth={1.8} /></div>
      </div>
      <div className="mt-4 text-3xl md:text-4xl nds-num tabular-nums" data-testid={`grant-value-${id}`}>{fmtGrant(t)}{t.format === "score" && <span className="text-base text-nex-muted"> /100</span>}</div>
      {id === "conduct" && <div className="mt-1"><Trend delta={t.delta} /></div>}
      <div className="mt-2 text-[0.7rem] text-nex-chrome" data-testid={`grant-sub-${id}`}>{t.sub}</div>
      {t.note && <div className="mt-1 text-[0.6rem] uppercase tracking-[0.14em] font-mono text-amber-400/90" data-testid={`grant-note-${id}`}>{t.note}</div>}
      <div className="mt-auto pt-3 flex items-center gap-2">
        <span className="nds-pill nds-pill--live" style={{ padding: "3px 8px", fontSize: "0.6rem" }}>LIVE</span>
        <span className="font-mono text-[0.6rem] text-nex-muted tracking-widest uppercase">· {t.window === "current" ? "current" : "trailing 30d"}</span>
        <span className="ml-auto inline-flex items-center gap-1 font-mono text-[0.6rem] tracking-[0.18em] uppercase text-nex-chrome">Open <ArrowUpRight className="w-3 h-3" /></span>
      </div>
    </button>
  );
}

function RowHeader({ kicker, right, testid }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2" data-testid={testid}>
      <div className="nds-label-dim">{kicker}</div>
      <div className="flex items-center gap-3">{right}</div>
    </div>
  );
}

/** Tier 1 · Grants — funder outcomes at one glance. Service value is a KPI, never cash. */
export function GrantsKpiRow() {
  const { data, error } = useGrantOutcomes(null);
  const { openWindow } = useFloatingWindows();
  const tiles = data?.tiles || {};
  return (
    <div className="flex flex-col gap-4" data-testid="nds-grants-row">
      <RowHeader testid="nds-grants-header" kicker="// GRANTS · FUNDER OUTCOMES" right={<>
        <span className="font-mono text-[0.7rem] text-nex-chrome tracking-[0.18em] uppercase hidden sm:inline">One glance</span>
        <button type="button" onClick={() => openWindow("grant_report", { title: "Grant report" })} data-testid="grant-report-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-nex-gold/60 text-nex-gold font-mono text-[0.6rem] tracking-[0.18em] uppercase rounded-sm hover:bg-nex-gold/10 transition-colors"><FileText className="w-3.5 h-3.5" /> Grant report</button>
      </>} />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {data ? GRANT_ORDER.filter((k) => tiles[k]).map((k) => <Tile key={k} id={k} t={tiles[k]} onOpen={() => openWindow("grant_tile", { key: k, title: `Grants · ${tiles[k].label}` })} />)
          : <div className="sm:col-span-2 xl:col-span-5 nds-card p-6 text-center font-mono text-sm text-nex-chrome" data-testid="nds-grants-loading">{error || "Loading grant outcomes…"}</div>}
      </div>
    </div>
  );
}

function ActivePartnersTile() {
  const [t, setT] = useState(null);
  useEffect(() => {
    const load = () => apiClient.get("/desk/pulse/partners").then(({ data }) => setT(data.tiles.active_partners)).catch(() => setT({ error: true }));
    load(); const id = setInterval(load, 30000); return () => clearInterval(id);
  }, []);
  return (
    <div className="nds-card p-5 text-nex-navy flex flex-col" data-testid="partner-tile-active">
      <div className="flex items-start justify-between gap-2">
        <div className="nds-label">Active partners</div>
        <div className="w-9 h-9 shrink-0 grid place-items-center bg-nex-navy text-nex-goldbright rounded-sm"><Building2 className="w-4 h-4" strokeWidth={1.8} /></div>
      </div>
      <div className="mt-4 text-3xl md:text-4xl nds-num tabular-nums" data-testid="partner-value-active">{t?.error ? "—" : t ? t.value : "…"}</div>
      <div className="mt-2 text-[0.7rem] text-nex-chrome" data-testid="partner-sub-active">{t?.error ? "Couldn't load this number" : t?.sub}</div>
      <div className="mt-auto pt-3"><span className="nds-pill nds-pill--live" style={{ padding: "3px 8px", fontSize: "0.6rem" }}>LIVE</span></div>
    </div>
  );
}

/** Tier 1 · Partners — owner-confirmed tiles only. */
export function PartnersKpiRow({ tiles = [<ActivePartnersTile key="active" />] }) {
  return (
    <div className="flex flex-col gap-4" data-testid="nds-partners-row">
      <RowHeader testid="nds-partners-header" kicker="// PARTNERS · PARTNER OUTCOMES" right={<span className="font-mono text-[0.7rem] text-nex-chrome tracking-[0.18em] uppercase hidden sm:inline">One glance</span>} />
      {tiles.length ? <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">{tiles}</div> : (
        <div className="nds-card p-5 flex items-center gap-3 text-nex-chrome" data-testid="nds-partners-empty">
          <Building2 className="w-5 h-5 text-nex-muted shrink-0" />
          <span className="text-sm">No partner tiles yet. They&apos;ll appear here once the tile list is confirmed.</span>
        </div>
      )}
    </div>
  );
}
